import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import axios from 'axios';
import { User } from '../auth/types';
import * as authService from '../services/authService';
import { clearScoutingReferenceCopies } from '../services/referenceCopies';
import i18n, { getUserLangKey } from '../i18n';

const applyUserLanguage = (user: User) => {
  const saved = localStorage.getItem(getUserLangKey(user.id));
  i18n.changeLanguage(saved || 'en');
};

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (identifier: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  revalidateOnReconnect: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const revalidationInFlight = useRef<Promise<void> | null>(null);

  const updateIdentity = useCallback((newUser: User | null) => {
    setUser(prev => {
      if (JSON.stringify(prev) === JSON.stringify(newUser)) return prev;
      return newUser;
    });
    if (newUser) {
      localStorage.setItem('citrus_user', JSON.stringify(newUser));
      applyUserLanguage(newUser);
    } else {
      localStorage.removeItem('citrus_user');
      localStorage.removeItem('token');
      sessionStorage.removeItem('citrus_user');
      i18n.changeLanguage('en');
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const userData = await authService.getMe();
      updateIdentity(userData);
    } catch (err) {
      if ((err as any).response?.status === 401) {
        updateIdentity(null);
      }
      throw err;
    }
  }, [updateIdentity]);

  const login = async (identifier: string, password: string, rememberMe: boolean = true) => {
    const data = await authService.login(identifier, password);
    setUser(data.user);
    applyUserLanguage(data.user);
    if (rememberMe) {
      localStorage.setItem('citrus_user', JSON.stringify(data.user));
      sessionStorage.removeItem('citrus_user');
      if (data.token) {
        localStorage.setItem('token', data.token);
      }
    } else {
      localStorage.removeItem('citrus_user');
      // Kept for this session only (like the token) so the scouting upload
      // can still tell which farm is signed in.
      sessionStorage.setItem('citrus_user', JSON.stringify(data.user));
      if (data.token) {
        sessionStorage.setItem('token', data.token);
      }
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } finally {
      updateIdentity(null);
      // The next person on this phone may belong to another farm. Unsent
      // rounds, drafts and photos are not touched.
      try {
        await clearScoutingReferenceCopies();
      } catch (err) {
        console.warn('[Auth] Could not clear the scouting reference copies', err);
      }
    }
  };

  const revalidateOnReconnect = useCallback((): Promise<void> => {
    if (revalidationInFlight.current) return revalidationInFlight.current;
    const p = refreshUser()
      .catch(() => {})
      .finally(() => {
        revalidationInFlight.current = null;
      });
    revalidationInFlight.current = p;
    return p;
  }, [refreshUser]);

  useEffect(() => {
    // Global 401 interceptor — defers to revalidation instead of immediate logout
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          revalidateOnReconnect().catch(() => {});
        }
        return Promise.reject(error);
      }
    );

    const initAuth = async () => {
      const savedUser = localStorage.getItem('citrus_user');
      if (savedUser) {
        try {
          const parsed = JSON.parse(savedUser);
          setUser(parsed);
        } catch (err) {
          localStorage.removeItem('citrus_user');
        }
      }

      try {
        await refreshUser();
      } catch (err) {
        console.log('User not authenticated on init');
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [revalidateOnReconnect, refreshUser]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, revalidateOnReconnect }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
